<template lang="pug">
	.station-log-player.full-height.full-width.flex-row
		Loader(v-if="isLoadingChunks")
		.flex-col(v-else)
			.flex-col.space(v-if="playingStation")
				.flex-col.space
					.font-small.upper.grey {{$tCaption('Playing now')}}:
					.flex-row.space.align-center
						img.station-logo(v-if="playingStationLogoUrl", :src="playingStationLogoUrl")
						.flex-col.station-info
							.station-name.nowrap.overflow {{playingStation && playingStation.Name}}
							.slogan.nowrap.overflow(v-if="playingStation && playingStation.Slogan") {{playingStation.Slogan}}
							.play-date {{$moment(dateTime).format('LLL')}}
			.canvas-holder
				canvas.data-canvas(ref="canvas", :width="`${canvas.width}px`", :height="`${canvas.height}px`", :style="{ width: `${canvas.width}.px`, height: `${canvas.height}px` }")
			.flex-row.align-center
				span {{currentReadableTime}}
				DxSlider.flex-1(:min="0", :max="totalDuration()", :step="1", :value="currentPosition", :width="200", @valueChanged="onSliderValueChanged")
				.play-button(@click="() => { stop(); $emit('close'); }")
					i.fa.fa-stop
</template>

<script>
	import VueTypes from 'vue-types';
	import _ from 'lodash';
	import { DxSlider } from 'devextreme-vue/slider';
	import Loader from 'app-core/Loader.vue';

	export default {
		translationCategory: 'logs.StationLogPlayer',
		name: 'StationLogPlayer',
		components: {
			DxSlider, Loader
		},
		props: {
			stationId: VueTypes.string.def(null),
			dateTime: VueTypes.any.def(null)
		},
		data() {
			return {				
				isLoading: false,
				audioContext: new AudioContext(),
				audioChunks: [],
				audioSources: [],
				isLoadingChunks: false,
				canvas: {
					height: 60,
					width: 220
				},
				analyser: null,
				freqs: null,
				times: null,
				playingStation: null,
				playState: 0,
				startTime: 0,
				currentContextTime: 0,
				offsetTime: 0,
				playStationLogData: null,
				hasNextChunks: null,
				isLoadingNextChunks: false
			};
		},
		computed: {			
			playingStationLogoUrl() {
				return this.$Data.getMethodUrl(`externalApi?accessToken=${this.$auth.accessToken()}&method=Station/GetStationImage&parameters=${encodeURIComponent(`stationId=${this.stationId}`)}&ver=${Math.random()}`);
			},
			currentPosition() {
				return this.currentContextTime - this.startTime;
			},
			currentReadableTime() {
				return this.$moment(0).add('seconds', this.currentPosition).format('mm:ss');
			},
		},
		watch: {
			currentPosition() {
				this.checkNextChunkLoading();
			}
		},
		created() {		
		},
		mounted() {
			if (__DEBUG__)
				window.stationLogPlayer = this;

			this.initAudioContextData();
			this.$currentTimeUpdater = setInterval(() => this.currentContextTime = this.audioContext.currentTime, 10);
			this.$playChunks = _.debounce(this.playChunks, 100, {
				'leading': false,
				'trailing': true,
				'maxWait': 1000
			});
			this.playStationLog();
		},
		destroyed() {
			this.stop();
		},
		deactivated() {
			this.stop();
		},
		methods: {
			totalDuration() {
				return this.audioChunks.reduce((total, s) => total += s.duration, 0) || 0;
			},
			initAudioContextData() {
				this.analyser = this.audioContext.createAnalyser();

				this.analyser.connect(this.audioContext.destination);
				this.analyser.minDecibels = -140;
				this.analyser.maxDecibels = 0;

				this.freqs = new Uint8Array(this.analyser.frequencyBinCount);
				this.times = new Uint8Array(this.analyser.frequencyBinCount);
			},
			draw() {
				if (!this.analyser || !this.$refs.canvas) return;
				var SMOOTHING = 0.8;
				var FFT_SIZE = 2048;
				this.analyser.smoothingTimeConstant = SMOOTHING;
				this.analyser.fftSize = FFT_SIZE;

				// Get the frequency data from the currently playing music
				this.analyser.getByteFrequencyData(this.freqs);
				this.analyser.getByteTimeDomainData(this.times);

				var drawContext = this.$refs.canvas.getContext('2d');
				drawContext.clearRect(0, 0, this.canvas.width, this.canvas.height);

				// Draw the time domain chart.
				for (let k = 0; k < this.analyser.frequencyBinCount; k++) {
					let value = this.times[k];
					let percent = value / 256;
					let height = this.canvas.height * percent;
					let offset = this.canvas.height - height - 1;
					let barWidth = this.canvas.width / this.analyser.frequencyBinCount;
					drawContext.fillStyle = '#f27649';
					drawContext.fillRect(k * barWidth, offset, 1, 1);
				}
			},		
			animateCanvas(val) {
				if (this.$animateCanvasInterval)
					clearInterval(this.$animateCanvasInterval);
				if (val) {
					setInterval(() => {
						if (this._isDestroyed) return;
						this.draw();
					}, 1000 / 60);
				}
			},
			playStationLog() {
				if (__DEBUG__)
					console.debug('playStationLog');
				const stationId = this.stationId;
				const dateTime = this.dateTime;				
				this.animateCanvas(true);
				this.isLoadingChunks = true;
				this.playingStation = null;
				this.logPlayerVisible = false;
				this.$Data.getExternalApi('Station/GetStationsByIds', `stationIds=${stationId}`, 1, true).then(r => {
					this.playingStation = r[0];
					this.refreshCanvasWidth();
				});
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.logPlayerVisible = true;
				});
				return (new Promise((mainRes, mainRej) =>
					this.$Data.getAzureStationLog(stationId, dateTime).then(r => {
						if (this._isDestroyed) return;
						this.playStationLogData = r;
						this.lastChunk = r.lastChunk;
						this.audioChunks = [];
						this.hasNextChunks = !!r.data.length;
						var promises = (r.data || []).map(data => new Promise(res => {
							var fr = new FileReader();
							fr.readAsArrayBuffer(data.blob);
							fr.onloadend = (evt) => {
								return this.audioContext.decodeAudioData(evt.target.result, (buffer) => {
									if (__DEBUG__)
										console.debug(`Audio decoded ${data.chunk}`);
									this.audioChunks[data.idx] = buffer;
									res(buffer);
								});
							};
							return null;
						}));

						Promise.all(promises).finally(() => mainRes(r));
						return null;
					}).catch(ex => {
						this.audioChunks = [];
						this.$emit('close');
						mainRej(ex);
					})
				))
				.then(() => {
					this.playChunks(this.$moment(dateTime).seconds());
				})
				.finally(() => {
					this.isLoadingChunks = false;
				});
			},
			playChunks(offsetTime) {
				if (__DEBUG__)
					console.debug('Play chunks', { offsetTime });
				var offset = (offsetTime || 0) * -1;
				var time = this.audioContext.currentTime + offset;
				this.startTime = this.audioContext.currentTime + offset;
				this.offsetTime = offsetTime;
				this.stop();
				this.playState = 1;
				this.audioSources = this.audioChunks.map(chunk => {
					const source = this.audioContext.createBufferSource();
					source.buffer = chunk;
					var startTime = time < 0 ? 0 : time;
					var startOffset = offset < 0 ? Math.abs(offset) : 0;

					time += chunk.duration;
					offset += chunk.duration;

					if (offset > 0) {
						source.connect(this.analyser);
						source.start(startTime, startOffset);
					}

					return source;
				});
				this.refreshCanvasWidth();
			},
			stop() {
				this.playState = 0;
				this.audioSources.map(f =>{
					try {
						f.stop()
					} catch {
						//
					}
				});
				this.animateCanvas(false);
			},
			onSliderValueChanged(e) {
				if (!e.event) return;
				if (__DEBUG__)
					console.debug(e);
				this.$playChunks(e.value);
			},
			refreshCanvasWidth() {
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.canvas.width = this.$el.offsetWidth;
				});
			},
			checkNextChunkLoading() {
				if (this.isLoadingNextChunks || !this.playStationLogData || !this.hasNextChunks || !this.playState) return;
				var deltaToEnd = this.totalDuration() - this.currentPosition;
				if (deltaToEnd < this.playStationLogData.chunkSize && this.lastChunk < this.playStationLogData.maxChunk) {
					if (__DEBUG__)
						console.debug('Loading next chunk', { deltaToEnd, chunkSize: this.playStationLogData.chunkSize, lastChunk: this.lastChunk, maxChunk: this.playStationLogData.maxChunk });
					this.isLoadingNextChunks = true;
					this.playStationLogData.loadChunk(void 0, void 0, this.lastChunk + 1).then(data => {
						if (this._isDestroyed) return;
						if (data && data.blob) {
							var fr = new FileReader();
							fr.readAsArrayBuffer(data.blob);
							fr.onloadend = (evt) => {
								return this.audioContext.decodeAudioData(evt.target.result, (buffer) => {
									if (__DEBUG__)
										console.debug(`Audio decoded ${data.chunk}`);
									this.audioChunks[this.audioChunks.length] = buffer;
									this.playChunks(this.currentPosition);
									this.isLoadingNextChunks = false;
								});
							};
							this.lastChunk++;
							if (this.lastChunk === this.playStationLogData.maxChunk)
								this.hasNextChunks = false;
						} else {
							this.hasNextChunks = false;
							this.isLoadingNextChunks = false;
						}
						return null;
					}).catch(() => this.isLoadingNextChunks = false);
				}
			},
		}
	}
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.station-log-player {
		position: relative;
		min-width: 300px;

		.play-button {
			min-width: 20px;
			color: #aaa;
			cursor: pointer;


			&:hover, &.hover {
				color: $base-accent;
			}
		}

		.station-info {
			max-width: 400px;
		}

		.station-name {
			font-size: 14px;
			font-weight: 800;
		}

		.play-date {
			font-size: 12px;
		}

		.station-logo {
			max-height: 60px;
			max-width: 120px;
			height: 60px;
		}
	}
</style>
